import {
    keepPreviousData,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateContractDeliveryRequest,
    ListContractDeliveryParams,
} from '@/lib/api/contract-delivery-api'

import { contractDeliveryApi } from '@/lib/api/contract-delivery-api'
import { tripKeys } from '@/hooks/use-delivery-trips-query'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

const errorToast = (fallback: string) => (err: unknown) => {
    const payload = formatApiError(err, fallback)
    toast.error(payload.title, { description: payload.description })
}

export const cdoKeys = {
    all: ['contract-delivery-orders'] as const,
    lists: () => [...cdoKeys.all, 'list'] as const,
    list: (params: ListContractDeliveryParams) =>
        [...cdoKeys.lists(), params] as const,
    detail: (id: string) => [...cdoKeys.all, 'detail', id] as const,
    timeline: (id: string) => [...cdoKeys.all, 'timeline', id] as const,
}

export function useContractDeliveries(params?: ListContractDeliveryParams) {
    return useQuery({
        queryKey: cdoKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await contractDeliveryApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

/**
 * Đơn giao hợp đồng tải theo trang — màn "Đơn chờ nhận" (FD-396), cùng cách
 * với useInfiniteSalesOrders.
 */
export function useInfiniteContractDeliveries(
    params: Omit<ListContractDeliveryParams, 'page' | 'per_page'>,
    perPage: number,
) {
    return useInfiniteQuery({
        queryKey: [...cdoKeys.lists(), 'infinite', params, perPage] as const,
        queryFn: async ({ pageParam }) => {
            const res = await contractDeliveryApi.list({
                ...params,
                page: pageParam,
                per_page: perPage,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
            lastPage.meta && lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined,
        placeholderData: keepPreviousData,
    })
}

export function useContractDelivery(id: string) {
    return useQuery({
        queryKey: cdoKeys.detail(id),
        queryFn: async () => {
            const res = await contractDeliveryApi.get(id)
            return res.data
        },
        enabled: !!id,
    })
}

export function useContractDeliveryTimeline(id: string) {
    return useQuery({
        queryKey: cdoKeys.timeline(id),
        queryFn: async () => {
            const res = await contractDeliveryApi.timeline(id)
            return res.data ?? []
        },
        enabled: !!id,
    })
}

// invalidate detail + list + timeline sau mỗi mutation.
function useInvalidateCDO() {
    const qc = useQueryClient()
    return (id?: string) => {
        void qc.invalidateQueries({ queryKey: cdoKeys.lists() })
        if (id) {
            void qc.invalidateQueries({ queryKey: cdoKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: cdoKeys.timeline(id) })
        }
    }
}

export function useCreateContractDelivery() {
    const invalidate = useInvalidateCDO()
    return useMutation({
        mutationFn: (data: CreateContractDeliveryRequest) =>
            contractDeliveryApi.create(data),
        onSuccess: (res) => {
            invalidate(res.data?.id)
            toast.success(m.cdo_toast_create_ok())
        },
        onError: errorToast(m.cdo_toast_create_err()),
    })
}

export function useUpdateContractDelivery() {
    const invalidate = useInvalidateCDO()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: CreateContractDeliveryRequest
        }) => contractDeliveryApi.update(id, data),
        onSuccess: (_res, { id }) => {
            invalidate(id)
            toast.success(m.cdo_toast_update_ok())
        },
        onError: errorToast(m.cdo_toast_update_err()),
    })
}

export function useConfirmContractDelivery() {
    const invalidate = useInvalidateCDO()
    return useMutation({
        mutationFn: (id: string) => contractDeliveryApi.confirm(id),
        onSuccess: (_res, id) => {
            invalidate(id)
            toast.success(m.cdo_toast_confirm_ok())
        },
        onError: errorToast(m.cdo_toast_confirm_err()),
    })
}

export function useCancelContractDelivery() {
    const invalidate = useInvalidateCDO()
    return useMutation({
        mutationFn: ({ id, note }: { id: string; note?: string }) =>
            contractDeliveryApi.cancel(id, { note }),
        onSuccess: (_res, { id }) => {
            invalidate(id)
            toast.success(m.cdo_toast_cancel_ok())
        },
        onError: errorToast(m.cdo_toast_cancel_err()),
    })
}

export function useAssignContractDeliveryDriver() {
    const invalidate = useInvalidateCDO()
    return useMutation({
        mutationFn: ({ id, driverId }: { id: string; driverId: string }) =>
            contractDeliveryApi.assignDriver(id, { driver_id: driverId }),
        onSuccess: (_res, { id }) => {
            invalidate(id)
            toast.success(m.cdo_toast_assign_ok())
        },
        onError: errorToast(m.cdo_toast_assign_err()),
    })
}

export function usePickupContractDelivery() {
    const invalidate = useInvalidateCDO()
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => contractDeliveryApi.pickupOrder(id),
        onSuccess: (_res, id) => {
            invalidate(id)
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            toast.success(m.cdo_toast_pickup_ok())
        },
        onError: errorToast(m.cdo_toast_pickup_err()),
    })
}

export function useStartContractDelivery() {
    const invalidate = useInvalidateCDO()
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, vehicleId }: { id: string; vehicleId?: string }) =>
            contractDeliveryApi.startDelivery(id, { vehicle_id: vehicleId }),
        onSuccess: (_res, { id }) => {
            invalidate(id)
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            toast.success(m.cdo_toast_start_ok())
        },
        onError: errorToast(m.cdo_toast_start_err()),
    })
}

export function useConfirmContractDeliveryDone() {
    const invalidate = useInvalidateCDO()
    const qc = useQueryClient()
    return useMutation({
        // KHÔNG payload tiền.
        mutationFn: (id: string) => contractDeliveryApi.confirmDelivery(id),
        onSuccess: (_res, id) => {
            invalidate(id)
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            toast.success(m.cdo_toast_done_ok())
        },
        onError: errorToast(m.cdo_toast_done_err()),
    })
}

export function useFailContractDelivery() {
    const invalidate = useInvalidateCDO()
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, note }: { id: string; note: string }) =>
            contractDeliveryApi.failDelivery(id, { note }),
        onSuccess: (_res, { id }) => {
            invalidate(id)
            void qc.invalidateQueries({ queryKey: tripKeys.active() })
            toast.success(m.cdo_toast_fail_ok())
        },
        onError: errorToast(m.cdo_toast_fail_err()),
    })
}
